import React, { useEffect, useRef, useState } from 'react';
import { dataUrl, evidenceRecord, fetchJson, readViewValue, RESEARCH_PROFILES, resolvePortalUrl, SECTION_HASHES, sectionFromHash, viewUrl } from './research.mjs';
import './research.css';
import { createDemandCache } from './demand-cache.mjs';

export const PORTAL_URL = resolvePortalUrl(import.meta.env.VITE_EIT_PORTAL_URL || import.meta.env.VITE_PORTAL_URL, typeof window === 'undefined' ? '' : window.location.hostname);
export const researchDataUrl = (path) => dataUrl(path, import.meta.env.VITE_DATA_BASE_URL || '');
const demandCache = createDemandCache((url) => fetchJson(url, { geojson: true }));
export function readDemandGeojson(path) {
  const url = researchDataUrl(path);
  return demandCache.read(url);
}
export function useDemandGeojson(path, enabled, setData, setErrors) {
  const [status, setStatus] = useState('pending');
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const url = researchDataUrl(path);
    setStatus('loading');
    readDemandGeojson(path).then((data) => { if (!cancelled) { setData(data); setStatus('ready'); } }).catch((error) => { if (!cancelled) { setStatus('error'); setErrors((previous) => [...new Set([...previous, error.message])]); } });
    return () => { cancelled = true; };
  }, [path, enabled]);
  return status;
}
export function useDeferredSections(sections) {
  const visited = useRef({});
  for (const [key, open] of Object.entries(sections)) if (open) visited.current[key] = true;
  return visited.current;
}

function useDialogAccessibility() {
  useEffect(() => {
    let activeDialog = null;
    let returnTarget = null;
    const update = () => {
      const dialogs = [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')].filter((dialog) => dialog.getClientRects().length);
      const next = dialogs.at(-1) || null;
      if (next === activeDialog) return;
      if (!next) {
        if (returnTarget?.isConnected) returnTarget.focus({ preventScroll: true });
        activeDialog = null; returnTarget = null; return;
      }
      if (!activeDialog) returnTarget = document.activeElement;
      activeDialog = next;
      if (!next.hasAttribute('aria-label') && !next.hasAttribute('aria-labelledby')) next.setAttribute('aria-label', next.querySelector('h1,h2')?.textContent || 'Sección de investigación');
      next.setAttribute('tabindex', '-1');
      if (!next.contains(document.activeElement)) next.focus({ preventScroll: true });
    };
    const trap = (event) => {
      if (event.key !== 'Tab' || !activeDialog) return;
      const items = [...activeDialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]')].filter((element) => element.getClientRects().length);
      const first = items[0], last = items.at(-1);
      if (!first) { event.preventDefault(); activeDialog.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) { event.preventDefault(); first.focus(); }
    };
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('keydown', trap);
    update();
    return () => { observer.disconnect(); document.removeEventListener('keydown', trap); };
  }, []);
}

// Only named fields are restored. Unknown query parameters and established hash routes survive.
export function useSharedView(fields) {
  const latest = useRef(fields);
  latest.current = fields;
  const defaults = useRef(Object.fromEntries(Object.entries(fields).map(([key, entry]) => [key, entry.value])));
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    const restore = () => {
      const params = new URLSearchParams(window.location.search);
      for (const [key, entry] of Object.entries(latest.current)) {
        let value = entry.restore ? entry.restore(params, defaults.current[key]) : readViewValue(params, key, defaults.current[key]);
        if (entry.validate && !entry.validate(value)) value = defaults.current[key];
        entry.set(value);
      }
    };
    restore();
    setHydrated(true);
    window.addEventListener('popstate', restore);
    return () => window.removeEventListener('popstate', restore);
  }, []);
  const encoded = JSON.stringify(Object.fromEntries(Object.entries(fields).map(([key, entry]) => [key, entry.value])));
  useEffect(() => {
    if (!hydrated) return;
    const values = JSON.parse(encoded);
    const compact = Object.fromEntries(Object.entries(values).map(([key, value]) => [key, !latest.current[key]?.persistDefault && JSON.stringify(value) === JSON.stringify(defaults.current[key]) ? null : value]));
    const next = viewUrl(window.location.href, compact);
    if (next.href !== window.location.href) window.history.replaceState(window.history.state, '', next);
  }, [encoded, hydrated]);
}

export function useSectionLinks(section, setSection) {
  const latestSetter = useRef(setSection);
  latestSetter.current = setSection;
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    const restore = () => latestSetter.current(sectionFromHash(window.location.hash));
    restore();
    setHydrated(true);
    window.addEventListener('popstate', restore);
    window.addEventListener('hashchange', restore);
    return () => { window.removeEventListener('popstate', restore); window.removeEventListener('hashchange', restore); };
  }, []);
  useEffect(() => {
    if (!hydrated) return;
    if (sectionFromHash(window.location.hash) === section) return;
    const url = new URL(window.location.href);
    url.hash = SECTION_HASHES[section] || '';
    window.history.pushState({ ...window.history.state, modal: section }, '', url);
  }, [section, hydrated]);
}

export function useChapterLink(chapters, index, setIndex, isOpen) {
  useSharedView({ capitulo: {
    value: chapters[index]?.id || '',
    persistDefault: true,
    set: (id) => { const next = chapters.findIndex((chapter) => chapter.id === id); if (next >= 0) setIndex(next); },
    validate: (id) => chapters.some((chapter) => chapter.id === id),
  } });
}

export function ResearchTools({ researchId, context = {}, count, errors = [], onOpenSection }) {
  useDialogAccessibility();
  const profile = RESEARCH_PROFILES[researchId];
  const [message, setMessage] = useState('');
  if (!profile) return null;
  const currentEvidence = evidenceRecord(profile, window.location.href, context);
  const download = () => {
    const record = evidenceRecord(profile, window.location.href, { ...context, visible_entity_count: count, data_base_url: import.meta.env.VITE_DATA_BASE_URL || `${window.location.origin}/data` });
    record.sources = record.sources.map((source) => ({ ...source, url: new URL(researchDataUrl(source.path), window.location.href).href }));
    const url = URL.createObjectURL(new Blob([JSON.stringify(record, null, 2)], { type: 'application/json' }));
    const anchor = document.createElement('a'); anchor.href = url; anchor.download = `${researchId}-ficha-evidencia.json`; anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setMessage('Ficha descargada con filtros, enlace, fuentes y límites.');
  };
  const share = async () => {
    try { await navigator.clipboard.writeText(window.location.href); setMessage('Enlace de la vista copiado.'); }
    catch { setMessage('Puedes copiar el enlace directamente desde la barra de direcciones.'); }
  };
  return <aside className="eit-research-tools" aria-label="Pregunta, evidencia y métodos">
    <details>
      <summary>Conocer para decidir · Pregunta y evidencia</summary>
      <div className="eit-research-body">
        <p className="eit-research-eyebrow">{profile.title}</p>
        <h2>{profile.question}</h2>
        <p><strong>Periodo seleccionado.</strong> {currentEvidence.observation_period} · Síntesis analítica: {currentEvidence.analytical_period}</p>
        {errors.length > 0 && <div role="alert" className="eit-research-warning"><strong>Datos incompletos.</strong> Algunas capas no se pudieron cargar. Su ausencia no significa cero ni falta de fenómenos.<ul>{errors.map((error) => <li key={error}>{error}</li>)}</ul><button type="button" onClick={() => window.location.reload()}>Reintentar carga</button></div>}
        <nav aria-label="Recorrido de investigación" className="eit-research-steps">
          {[['none', 'Observar en el mapa'], ['geostory', 'Comprender la historia'], ['dashboard', 'Contrastar indicadores'], ['report', 'Preparar la deliberación'], ['about', 'Revisar métodos']].map(([id, label]) => <button key={id} type="button" onClick={() => onOpenSection?.(id)}>{label}</button>)}
        </nav>
        <h3>Fuentes y preparación</h3><ul>{currentEvidence.sources.map((source) => <li key={source.path}><a href={researchDataUrl(source.path)} target="_blank" rel="noreferrer">{source.name}</a></li>)}</ul>
        <h3>Cómo leer los resultados</h3><ul>{profile.methods.map((text) => <li key={text}>{text}</li>)}</ul>
        <h3>Límites de la evidencia</h3><ul>{profile.limits.map((text) => <li key={text}>{text}</li>)}</ul>
        <h3>Preguntas para deliberar</h3><ul>{profile.deliberation.map((text) => <li key={text}>{text}</li>)}</ul>
        <p>Contrasta la observación, la interpretación y los documentos. Las decisiones corresponden a quienes viven y deliberan sobre el territorio.</p>
        <div className="eit-research-actions"><button type="button" onClick={share}>Copiar enlace de esta vista</button><button type="button" onClick={download}>Descargar ficha de evidencia</button><a href={new URL('#learn', PORTAL_URL).href}>Aprender a interpretar los datos</a></div>
        <p role="status" aria-live="polite">{message}</p>
      </div>
    </details>
  </aside>;
}

export function DataLoadNotice({ loading, errors = [] }) {
  if (loading) return <div className="eit-data-notice eit-loading-shield" role="status" aria-live="polite">Cargando datos del territorio…</div>;
  if (!errors.length) return null;
  return <div className="eit-data-notice eit-research-warning" role="alert"><strong>No se pudieron cargar todos los datos.</strong> Los resultados están incompletos; una capa ausente no representa cero. <button type="button" onClick={() => window.location.reload()}>Reintentar</button><details><summary>Ver capas pendientes</summary><ul>{errors.map((error) => <li key={error}>{error}</li>)}</ul></details></div>;
}

export function ChapterEvidenceNote({ chapter, researchId }) {
  const profile = RESEARCH_PROFILES[researchId];
  return <details className="eit-chapter-evidence"><summary>Fuente, interpretación y límites del capítulo</summary>
    <p>Este capítulo combina resultados cartográficos y contexto editorial. Sus cifras estáticas corresponden a la edición narrativa recibida; consulta el dashboard y sus fuentes para los indicadores calculados. Las afirmaciones históricas, jurídicas y causales requieren referencias documentales verificables, pendientes de incorporar cuando no se enlazan aquí. La coincidencia espacial o temporal requiere contraste y no demuestra por sí sola causalidad o situación jurídica.</p>
    {chapter?.quote && <p><strong>Cita atribuida: referencia pendiente.</strong> El material editorial recibido no incorpora una referencia documental y autorización verificables para esta cita. No debe utilizarse como testimonio validado.</p>}
    <p>{profile?.limits[0]}</p><p>Consulta las fuentes y métodos del proyecto antes de reutilizar cifras o afirmaciones de contexto.</p>
  </details>;
}

export function ReportEvidence({ researchId, context = {} }) {
  const profile = RESEARCH_PROFILES[researchId];
  const record = evidenceRecord(profile, typeof window !== 'undefined' ? window.location.href : 'https://geoeit.org/', context);
  return <section className="eit-report-evidence" aria-label="Alcance y trazabilidad del informe"><h2>Alcance, fuentes y límites</h2>
    <p><strong>Pregunta.</strong> {profile.question}</p><p><strong>Periodo seleccionado.</strong> {record.observation_period}</p><p><strong>Periodo analítico.</strong> {record.analytical_period}</p>
    <p><strong>Conjunto del informe.</strong> {Object.entries(context).map(([key, value]) => `${key}: ${value === '' || value === null || value === undefined ? 'Sin filtro' : String(value)}`).join(' · ')}</p>
    <p><strong>Enlace de consulta.</strong> <a href={typeof window !== 'undefined' ? window.location.href : '#'}>Recuperar esta vista con sus filtros</a></p>
    <p><strong>Fuentes preparadas.</strong> {record.sources.map((source) => `${source.name} · ${source.path}`).join(' · ')}</p>
    <p><strong>Identidad de archivos cargados.</strong> {record.hash_note}</p>
    <ul>{record.loaded_assets.map((asset) => <li key={asset.path}>{asset.path} · SHA-256: {asset.sha256 || 'No disponible'} · Procesamiento: {asset.processed_at || 'No documentado'}{asset.run_key ? ` · run_key: ${asset.run_key}` : ''}</li>)}</ul>
    <ul>{profile.limits.map((text) => <li key={text}>{text}</li>)}</ul>
    <p>La coincidencia espacial no determina causalidad o legalidad. Este documento apoya el contraste y la deliberación territorial.</p>
  </section>;
}
